/**
 * Bootstrap and application shell.
 *
 * Owns four things: get a canvas, run the scene machine, apply settings, and
 * make failures legible.
 *
 * The error boundary matters more than it looks. The most common way a WebGL
 * game fails for a real user is a black screen with a console message they will
 * never open, and "it didn't work" is an unactionable bug report. Anything
 * thrown during boot gets rendered into the page with its stack.
 *
 * **The scene machine is the point of this file.** It used to be a chain of
 * early returns and nested closures — prologue, then setup, then a Game, with
 * no way back to anywhere and no title screen at all — and every new screen
 * would have added another boolean to it. `ui/scene.ts` holds the whole legal
 * transition table; `enter()` below is the only place that turns a scene into
 * DOM, and it is exhaustive over the union, so a new scene is a compile error
 * until it is handled.
 *
 * Note the deliberate split between two things both called "scene":
 *   - `AppScene` (here) — the screen the player is looking at.
 *   - `Game.scene` — which world to build: 'world', 'lab' or 'prologue'. Fixed
 *     at construction, never changed. The two meet in `startGame` and nowhere
 *     else.
 */

import { Game } from './core/game';
import { profiler } from './core/profiler';
import {
  installRouter,
  parseRoute,
  setRoute,
  workshopDocPath,
  workshopRouteFor,
  type RouteIntent,
  type WorkshopDoc,
} from './core/router';
import { describeOffer, rollDeltas } from './world/weaponAltar';
import { ABILITY_TEXT } from './world/abilityCache';
import { claimRegion } from './ui/hudLayout';
import { RunSetupScreen, injectRunSetupStyle } from './ui/runSetup';
import { buildRunSetupVM, buildTitleVM, resolveStartAbilities } from './core/runSetupAdapter';
import { TitleScreen } from './ui/title';
import { SettingsScreen } from './ui/settings';
import { applyScene, initialScene, type SceneEvent, type SceneState } from './ui/scene';
import {
  SHELL_PAD_SCENE,
  ShellPad,
  padKeyEvent,
  type ShellCommand,
  type ShellScene,
} from './ui/menuPad';
import {
  CONTRAST_VARS,
  bindingRows,
  padBindingRows,
  padDeadzoneFor,
  readBindOverrides,
  readPadBindOverrides,
  readSettings,
  resolveBindings,
  resolvePadBindings,
  shakeScaleFor,
  uiScalePx,
  writeBindOverrides,
  writePadBindOverrides,
  writeSettings,
  type BindOverrides,
  type PadBindOverrides,
  type SettingId,
  type SettingsState,
} from './ui/settingsModel';
import { DEFAULT_BINDINGS, DEFAULT_PAD_BINDINGS, setPadDeadzone, type Input } from './core/input';
import { Progression } from './meta/progression';
import { getArchetype } from './meta/archetypes';
import { isResumable, type RunState } from './meta/runState';
import { SaveSystem, createProfile, type ProfileData } from './meta/save';
import { buyFromAshShop } from './meta/shop';
import { worldCoordFaultLine } from './world/coordGuard';

const boot = document.getElementById('boot')!;
const canvas = document.getElementById('game') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui-root')!;

/** Event lines are game-generated, but escaping keeps innerHTML honest. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}

/**
 * The debug overlay's non-finite report — issue #76, and empty in a healthy run.
 *
 * Two records, because they answer different questions. `camera.faults` says a
 * *coordinate source* was handed something that is not a number, and names the
 * first offending term — that is the producer, or the closest this build can get
 * to it. `worldCoordFaults` says a non-finite pair reached a **world lookup**
 * anyway, which after #71's total sanitiser is silent everywhere else: the
 * lookup returns a perfectly ordinary biome for a place that does not exist.
 *
 * Loud without a clock or a console, and it stays loud: neither record resets.
 */
function nonFiniteLine(game: Game): string {
  const f = game.camera.faults;
  const world = worldCoordFaultLine();
  if (f.count === 0) return world;
  const cam =
    `!! camera refused ${f.count} non-finite input(s) — ` +
    `first ${f.first}=${f.firstValue}, last ${f.last}=${f.lastValue}`;
  return world ? `${cam}\n  ${world}` : cam;
}

function fail(err: unknown): void {
  const message = err instanceof Error ? `${err.message}\n\n${err.stack ?? ''}` : String(err);
  boot.classList.remove('gone');
  boot.innerHTML = '';
  const div = document.createElement('div');
  div.className = 'err';
  div.textContent = `Noitroidvania failed to start.\n\n${message}`;
  boot.appendChild(div);
  console.error(err);
}

// --------------------------------------------------------------------- shell

interface StartChoice {
  archetype: string;
  kit: string;
  mode: string;
  abilities: string[];
  seed: number;
  scene: 'world' | 'lab';
  /**
   * The run to resume, if this is a Continue (#51). Absent for a new run, in
   * which case `Game` creates one. The five fields above are derived *from* it
   * when it is present, so the two can never describe different runs.
   */
  run?: RunState;
}

/**
 * Everything the running Game hangs off. Torn down as one thing by `stopGame`,
 * because a partial teardown is how the previous shape of this file would have
 * leaked a rAF loop per quit.
 */
interface LiveRun {
  game: Game;
  dispose: () => void;
}

/**
 * The address each screen answers to, total over `AppScene` so a scene added
 * later has to decide — the same trick `SHELL_PAD_SCENE` plays. `null` means
 * "no address of its own": the prologue and the setup screen are steps inside
 * an intent (see `core/router.ts`), so entering them leaves the URL at the
 * `/start` they were reached from, and a refresh mid-setup lands back on the
 * title rather than on a screen whose prologue precondition it cannot check.
 */
const ROUTE_FOR_SCENE: Record<SceneState['scene'], string | null> = {
  title: '/start',
  settings: '/settings',
  playing: '/play',
  prologue: null,
  runSetup: null,
};

class Shell {
  private state: SceneState = initialScene();

  private save = new SaveSystem();
  private profile: ProfileData;
  private prog: Progression;
  /**
   * The run on disk, if it can be resumed — issue #51.
   *
   * Every autosave wrote one and nothing ever read it back, which is what made
   * Continue impossible rather than merely absent. This field is that reader.
   * It is `null` when there is no run, or when the run on disk has an outcome:
   * `isResumable` owns that judgement and `meta/runState.ts` records, field by
   * field, what resuming does and does not restore.
   */
  private savedRun: RunState | null = null;
  private settings: SettingsState;
  /**
   * The player's key choices, as a diff against `DEFAULT_BINDINGS`. Stored
   * beside the settings in the same loose profile record, for the same reason.
   */
  private bindOverrides: BindOverrides = {};
  /** The pad twin of `bindOverrides` — issue #191. */
  private padBindOverrides: PadBindOverrides = {};
  /**
   * Every code the last `applyBindings` laid down. Kept so the next one can
   * clear it: an override that has since been removed points at a code that is
   * in neither the defaults nor the new overrides, and would otherwise stay
   * bound for the rest of the run.
   */
  private appliedCodes: string[] = [];
  /** The pad twin of `appliedCodes`. */
  private appliedPadButtons: number[] = [];

  private title: TitleScreen | null = null;
  private settingsScreen: SettingsScreen | null = null;
  /** The in-game editors — `src/editor/gameHost.ts`, loaded on first use. */
  private workshop: { root: HTMLElement; destroy(): void } | null = null;
  private setup: RunSetupScreen | null = null;
  private live: LiveRun | null = null;

  /**
   * The gamepad, on the three screens that exist outside a run — issue #78.
   *
   * `UIRoot`'s pump rides `Game.render`, and at the title screen there is no
   * `Game`, so this brings its own `requestAnimationFrame` loop. It is pointed
   * at a screen in exactly one place — `enter`, below — and pointing it at
   * nothing is what stops the loop; `ShellPad` documents why that is an
   * invariant of the object rather than a rule this file has to remember.
   */
  private shellPad = new ShellPad((cmd, scene) => this.runShellPadCommand(cmd, scene));

  /** 'keyboard' or 'pad' — see `setInputDevice`'s header for the heuristic. */
  private inputDevice: 'keyboard' | 'pad' = 'keyboard';

  /** Rolled once per visit to the setup screen; the reroll button replaces it. */
  private seed = rollSeed();
  /** #94 — held across runs so the same world can be played twice. */
  private seedLocked = false;

  constructor() {
    const loaded = this.save.load();
    this.profile = loaded.profile ?? createProfile();
    this.prog = new Progression(this.profile.progression);
    this.savedRun = isResumable(loaded.run) ? loaded.run : null;
    this.settings = readSettings(this.profile.settings);
    this.bindOverrides = readBindOverrides(this.profile.settings);
    this.padBindOverrides = readPadBindOverrides(this.profile.settings);
    this.applyGlobalSettings();
    // The keyboard/mouse half of `setInputDevice`'s signal — issue #193. Both
    // are genuine, always-trusted DOM events (see that method's header for why
    // a synthetic `padKeyEvent()` can never trigger these), attached once and
    // never removed: the shell outlives every scene it hosts.
    window.addEventListener('keydown', () => this.setInputDevice('keyboard'));
    window.addEventListener('pointerdown', () => this.setInputDevice('keyboard'));
  }

  // -- scene machine --------------------------------------------------------

  /**
   * Take one edge, tear the old screen down, build the new one.
   *
   * A refused transition is logged rather than swallowed: a menu that silently
   * ignores a button is indistinguishable from one that is broken.
   */
  dispatch(event: SceneEvent): void {
    const next = applyScene(this.state, event, {
      prologueSeen: this.profile.settings.prologueSeen === true,
    });
    if (!next) {
      console.warn(`[shell] ${event} is not legal from ${this.state.scene}`);
      return;
    }
    if (next.scene === this.state.scene && next.returnTo === this.state.returnTo) return;
    this.exit(this.state.scene, next.scene);
    this.state = next;
    this.enter(next.scene);
  }

  /**
   * Leave `from`. `to` matters for exactly one case: opening settings over a
   * paused run must not destroy the run.
   */
  private exit(from: SceneState['scene'], to: SceneState['scene']): void {
    switch (from) {
      case 'title':
        this.title?.hide();
        break;
      case 'settings':
        this.settingsScreen?.hide();
        break;
      case 'runSetup':
        this.setup?.hide();
        this.setup?.destroy();
        this.setup = null;
        break;
      case 'prologue':
      case 'playing':
        // Settings is an overlay on a paused run, not a replacement for it.
        if (to !== 'settings') this.stopGame();
        break;
    }
  }

  private enter(scene: SceneState['scene']): void {
    boot.classList.add('gone');
    // Keep the address bar on the screen being entered. A push, so Back walks
    // the screens the player actually visited; `setRoute` no-ops when the URL
    // is already there, which is exactly the popstate case — by the time the
    // handler dispatches, the browser has already moved the URL.
    const path = ROUTE_FOR_SCENE[scene];
    if (path !== null) setRoute(path);
    // Before the screen is built, and unconditionally — the one line that both
    // starts the shell's frame loop and stops it. `SHELL_PAD_SCENE` is total
    // over `AppScene`, so a scene added later cannot quietly skip this.
    // Entering `playing` or `prologue` maps to null, which cancels the loop and
    // hands the pad back to the run's own two readers.
    this.shellPad.setScene(SHELL_PAD_SCENE[scene]);
    switch (scene) {
      case 'title':
        this.showTitle();
        break;
      case 'settings':
        this.showSettings();
        break;
      case 'runSetup':
        this.showSetup();
        break;
      case 'prologue':
        this.startPrologue();
        break;
      case 'playing':
        // Nothing. `startGame` is called by whoever dispatched `runStarted` or
        // `openLab`, and returning here from settings deliberately leaves the
        // run exactly as it was: still paused, still showing the pause menu,
        // one Resume away. Unpausing from here would clear `Game.paused`
        // without telling `UIRoot`, which owns the pause panel and the input
        // gate — the game would run under a modal with input disabled.
        break;
    }
  }

  boot(): void {
    installRouter((intent) => this.applyIntent(intent));
    this.applyIntent(parseRoute(window.location.pathname));
  }

  /**
   * Make the shell match one parsed address — the boot path and the popstate
   * handler, one function, so a deep link and a Back press cannot disagree
   * about what an address means. Everything here moves through `dispatch`,
   * so the scene machine's transition table still rules on every hop; this
   * only chooses which edges to take.
   */
  private applyIntent(intent: RouteIntent): void {
    // The workshop is an overlay over the title, not a scene: any address
    // that is not its own takes it down first. Straight destruction rather
    // than the /quit route, because on a popstate the URL has already moved
    // and the page underneath is about to be re-entered anyway.
    if (intent.kind !== 'workshop' && this.workshop !== null) {
      this.workshop.destroy();
      this.workshop = null;
    }
    if (intent.kind === 'unknown') {
      // Off the table → `/start`, as a correction rather than a history stop.
      setRoute('/start', true);
      intent = { kind: 'start' };
    }
    switch (intent.kind) {
      case 'start':
        setRoute('/start', true); // canonicalise `/`
        this.toTitle(true);
        return;
      case 'settings':
        if (this.state.scene === 'settings') return;
        // `openSettings` is legal from the title and a paused run only —
        // from anywhere else, get to the title first so the edge exists.
        if (this.state.scene !== 'title' && this.state.scene !== 'playing') this.toTitle(false);
        this.dispatch('openSettings');
        return;
      case 'play':
        if (this.state.scene === 'playing') return;
        if (this.state.scene === 'settings' && this.state.returnTo === 'playing') {
          // Back out of settings over a paused run *is* returning to /play.
          this.dispatch('closeSettings');
          return;
        }
        if (isResumable(this.savedRun)) {
          this.toTitle(false);
          this.continueRun();
          return;
        }
        // `/play` with nothing to resume is a link to a run that does not
        // exist — the same answer as an unknown path.
        setRoute('/start', true);
        this.toTitle(true);
        return;
      case 'workshop':
        // The title stays mounted underneath the workshop, so build it.
        this.toTitle(true);
        void this.showWorkshop(intent.doc);
        return;
    }
  }

  /**
   * Get the scene machine to the title from wherever it is, in at most two
   * legal edges (settings first, since `quitToTitle` is not legal from it).
   * `show` builds the screen when the machine is *already* at the title but
   * nothing has been entered yet — the boot case; every other path enters
   * through `dispatch`.
   */
  private toTitle(show: boolean): void {
    if (this.state.scene === 'settings') this.dispatch('closeSettings');
    if (this.state.scene !== 'title') this.dispatch('quitToTitle');
    else if (show && this.title === null) this.enter('title');
  }

  /**
   * Hand one pad command to whichever shell screen is up — issue #78.
   *
   * The scene comes in with the command rather than being read off `this.state`
   * again: a `back` earlier in the same frame may already have moved the shell,
   * and `ShellPad` re-asks its own scene per intent, so this must act on the
   * screen the command was decided for or the second half of a frame lands on
   * the wrong one.
   *
   * The `?.` on all three is not defensiveness for its own sake — `runSetup`'s
   * screen is destroyed on exit and rebuilt on entry, so a frame in flight
   * across that edge genuinely finds nothing there.
   */
  private runShellPadCommand(cmd: ShellCommand, scene: ShellScene): void {
    // A command only reaches here once `ShellPad` has actually translated a
    // press, so this is the "the pad just drove something" edge the on-screen
    // hint switch keys off — see `setInputDevice`'s header.
    this.setInputDevice('pad');
    if (cmd.kind === 'group') {
      this.setup?.moveGroup(cmd.delta);
      return;
    }
    if (cmd.kind === 'reroll') {
      this.setup?.reroll();
      return;
    }
    const e = padKeyEvent(cmd.key);
    if (scene === 'title') this.title?.handlePadKey(e);
    else if (scene === 'settings') this.settingsScreen?.handlePadKey(e);
    else this.setup?.handlePadKey(e);
  }

  /**
   * Which device is currently driving the shell screens — issue #193.
   *
   * There was no such signal before this: `Input.ActionState.sources` records
   * who holds one *run* action, per action, which answers "is the pad held
   * right now" and not "which device did the player last touch", and neither
   * question is answerable at the title or run-setup screens anyway, since
   * neither has an `Input`. So this is a small equivalent at the shell level,
   * and it is deliberately the simplest signal that is still correct:
   *
   *   - **'pad'** the instant `runShellPadCommand` runs — a real translated
   *     pad press, not merely a connected controller resting untouched.
   *   - **'keyboard'** on the next real `keydown` or `pointerdown` anywhere in
   *     the window. Both are attached once, below, and both only ever see
   *     genuine trusted events: `padKeyEvent()` records are plain objects
   *     handed directly to a screen's `handlePadKey`, never `dispatchEvent`-ed,
   *     so they cannot loop back through these listeners and self-cancel.
   *
   * `title`/`setup` are `?.`'d for the same reason `runShellPadCommand` is —
   * the screen the device changed on may not be the one currently mounted.
   */
  private setInputDevice(next: 'keyboard' | 'pad'): void {
    if (this.inputDevice === next) return;
    this.inputDevice = next;
    this.title?.setPadActive(next === 'pad');
  }

  // -- title ----------------------------------------------------------------

  private showTitle(): void {
    if (!this.title) {
      this.title = new TitleScreen(uiRoot, {
        onChoose: (action) => {
          if (action === 'continue') this.continueRun();
          else if (action === 'newRun') this.dispatch('newRun');
          else if (action === 'settings') this.dispatch('openSettings');
          else if (action === 'lab') {
            this.dispatch('openLab');
            this.startGame({
              archetype: 'duelist',
              kit: this.prog.state.startKit,
              mode: 'layered',
              abilities: [],
              seed: rollSeed(),
              scene: 'lab',
            });
          } else if (action === 'workshop') void this.showWorkshop();
          else if (action === 'prologue') this.dispatch('openPrologue');
        },
      });
    }
    this.title.update(buildTitleVM(this.prog, this.bindOverrides, this.savedRun, this.padBindOverrides));
    // A screen the title never destroys still has to reflect the device that
    // changed while some *other* scene was up — `setInputDevice` only pushes
    // to the screen mounted at the time it fires.
    this.title.setPadActive(this.inputDevice === 'pad');
    this.title.show();
  }

  /**
   * The workshop — the four editors, over the title rather than a scene of
   * their own. A dynamic import because the editor chunk (pages, rasteriser,
   * help sources) is authoring tooling a player who never opens it should
   * never download; the title stays mounted underneath, so closing is a
   * removal rather than a scene transition.
   */
  private async showWorkshop(doc?: WorkshopDoc): Promise<void> {
    if (this.workshop) return;
    const { EditorHost, workshopAssets } = await import('./editor/gameHost');
    const assets = workshopAssets();
    // `/workshop/<kind>/<name>` resolves against the same registry list the
    // picker offers, so a link opens exactly what the build holds — and a
    // link to a document it does not hold is an unknown address, answered
    // with `/start` like every other one.
    const path = doc === undefined ? undefined : workshopDocPath(doc, assets);
    if (doc !== undefined && path === undefined) {
      setRoute('/start', true);
      return;
    }
    this.workshop = new EditorHost({
      path,
      // Documents opened from inside the workshop track in the address bar
      // as a replace, not a push: the picker is one surface, and Back should
      // leave the workshop rather than walk every document visited in it.
      onNavigate: (rel) => setRoute(workshopRouteFor(rel, assets), true),
      onClose: () => {
        this.workshop?.destroy();
        this.workshop = null;
        setRoute('/start', true);
      },
    });
    document.body.append(this.workshop.root);
    setRoute(path === undefined ? '/workshop' : workshopRouteFor(path, assets));
  }

  /**
   * Resume the run on disk — issue #51.
   *
   * The archetype, kit, world mode and seed are read off the run rather than
   * asked for again: they are what the run *is*, and the setup screen is
   * skipped for exactly that reason. `startAbilities` is empty because a
   * resumed run does not go through `createRunState` at all — its granted and
   * found abilities are already on the run, and re-deriving them from the
   * profile would hand back a set the run never had.
   */
  private continueRun(): void {
    const run = this.savedRun;
    // Belt and braces over the button's own absence. A resume with nothing to
    // resume must do nothing visible rather than silently start a new run,
    // which is the exact failure the missing button was avoiding.
    if (!isResumable(run)) {
      console.warn('[shell] nothing to continue');
      return;
    }
    this.dispatch('continueRun');
    this.startGame({
      archetype: run.archetype,
      kit: run.kit,
      mode: run.worldMode,
      abilities: [],
      seed: run.seed,
      scene: 'world',
      run,
    });
  }

  // -- settings -------------------------------------------------------------

  private showSettings(): void {
    if (!this.settingsScreen) {
      this.settingsScreen = new SettingsScreen(uiRoot, {
        onChange: (id, value) => this.changeSetting(id, value),
        onRebind: (action, code) => this.changeBinding(action, code),
        onRebindPad: (action, button) => this.changeBindingPad(action, button),
        // Issue #191. A capture row started or stopped listening for a raw pad
        // button; point `ShellPad`'s capture callback at the row (or take it
        // away) so the very next button reaches `capturePadButton` instead of
        // being read as menu navigation. The screen never touches a pad
        // itself — same separation `onRebindPad` keeps from the live `Input`.
        onPadListenChange: (active) => {
          if (active) this.shellPad.startButtonCapture((btn) => this.settingsScreen?.capturePadButton(btn));
          else this.shellPad.stopButtonCapture();
        },
        onClose: () => this.dispatch('closeSettings'),
      });
    }
    this.renderSettings();
    this.settingsScreen.show();
  }

  private renderSettings(): void {
    this.settingsScreen?.update(this.settings, {
      backLabel: this.state.returnTo === 'playing' ? 'Back to the run' : 'Back to title',
      bindings: bindingRows(DEFAULT_BINDINGS, this.bindOverrides),
      padBindings: padBindingRows(DEFAULT_PAD_BINDINGS, this.padBindOverrides),
      omissions: [
        'Audio — there is none yet. No AudioContext, no samples, nothing to set a level on.',
        'Movement keys — A/D and the arrow keys are the same two actions, so rebinding one would take the other away without saying so.',
        'Tab, Esc, C and M — ui/uiRoot.ts reads those codes directly as well as Input, so a rebind would take in one place and not the other.',
        'D-pad and menu buttons on the pad — up/down/left/right, confirm, back and the shoulders drive this screen and every menu behind it, so binding an action onto one would fight the row capturing it.',
      ],
    });
  }

  private changeSetting(id: SettingId, value: string): void {
    this.settings = { ...this.settings, [id]: value };
    this.persistSettings();
    this.applyGlobalSettings();
    this.onRunSettingsChanged?.();
    this.renderSettings();
  }

  /**
   * A key row moved. Persist it, push it at the live `Input` if there is one,
   * and re-render from the *resolved* table rather than from what was asked
   * for — giving jump the main attack's key leaves the main attack with none,
   * and the screen has to show that.
   */
  private changeBinding(action: string, code: string | null): void {
    const next: Record<string, string> = { ...this.bindOverrides };
    if (code === null) delete next[action];
    else next[action] = code;
    this.bindOverrides = next;
    writeBindOverrides(next, this.profile.settings);
    this.persist();
    this.applyBindings();
    // The in-run hint line reads off the same table, so it is corrected here
    // rather than on the next run.
    this.onRunSettingsChanged?.();
    this.renderSettings();
  }

  /** The pad twin of `changeBinding` — issue #191. */
  private changeBindingPad(action: string, button: number | null): void {
    const next: Record<string, number> = { ...this.padBindOverrides };
    if (button === null) delete next[action];
    else next[action] = button;
    this.padBindOverrides = next;
    writePadBindOverrides(next, this.profile.settings);
    this.persist();
    this.applyPadBindings();
    this.onRunSettingsChanged?.();
    this.renderSettings();
  }

  /**
   * Lay the resolved binding table over the live `Input`.
   *
   * Clear-then-set rather than set-only, because `Input.rebind` adds: without
   * the clearing pass, moving jump from Space to Z would leave Space jumping
   * too — which is the exact reason this screen had no rebinding row until
   * `Input.unbind` existed. Idempotent, so it is safe to call mid-run on every
   * change and again when the next run starts.
   */
  private applyBindings(): void {
    const input: Input | undefined = this.live?.game.input;
    if (!input) return;
    for (const code of this.appliedCodes) input.unbind(code);
    for (const code of Object.keys(DEFAULT_BINDINGS)) input.unbind(code);
    const resolved = resolveBindings(DEFAULT_BINDINGS, this.bindOverrides);
    for (const code in resolved) input.rebind(code, resolved[code]);
    this.appliedCodes = Object.keys(resolved);
  }

  /** The pad twin of `applyBindings`. */
  private applyPadBindings(): void {
    const input: Input | undefined = this.live?.game.input;
    if (!input) return;
    for (const button of this.appliedPadButtons) input.unbindPad(button);
    for (const button of Object.keys(DEFAULT_PAD_BINDINGS)) input.unbindPad(Number(button));
    const resolved = resolvePadBindings(DEFAULT_PAD_BINDINGS, this.padBindOverrides);
    for (const button in resolved) input.rebindPad(Number(button), resolved[button]);
    this.appliedPadButtons = Object.keys(resolved).map(Number);
  }

  /**
   * Write the settings through to the profile and to disk.
   *
   * This used to have a second half — the same write repeated after
   * `stopGame()` — because `Game` loaded its *own* `ProfileData` and autosaved
   * it, so a setting changed mid-run was reverted by the next in-run write.
   * `GameOptions.profile` retired that workaround (#51): the Game now mutates
   * this object, so a mid-run change is already in the copy the Game saves.
   *
   */
  private persistSettings(): void {
    writeSettings(this.settings, this.profile.settings);
    // The key choices ride along in the same loose record, for the same reason.
    writeBindOverrides(this.bindOverrides, this.profile.settings);
    writePadBindOverrides(this.padBindOverrides, this.profile.settings);
    this.persist();
  }

  /**
   * The shell's only write — issue #51.
   *
   * `SaveSystem.save` writes the *whole* envelope, so the five call sites that
   * used to pass a null run alongside the profile were each deleting the run
   * on disk: buying from the Ash shop at the setup screen, toggling a world
   * modifier, moving a key, changing a setting. Nothing read the run, so nothing
   * noticed. The moment Continue exists, "the player bought an unlock and lost
   * their run" is a bug report, so there is now one writer and it preserves the
   * run.
   *
   * With a Game live, that Game owns the run and already holds this same
   * `ProfileData` object, so it writes both halves; our `savedRun` is the run
   * from before it started and would revert it.
   */
  private persist(): void {
    if (this.live) {
      this.live.game.persist();
      return;
    }
    this.save.save(this.profile, this.savedRun);
  }

  /** The settings that touch the UI layer, which exists in every scene. */
  private applyGlobalSettings(): void {
    const s = uiRoot.style;
    s.setProperty('--ui-fs', `${uiScalePx(this.settings.uiScale)}px`);
    for (const k in CONTRAST_VARS) {
      if (this.settings.contrast === 'high') s.setProperty(k, CONTRAST_VARS[k]);
      else s.removeProperty(k);
    }
    // Global, like `--ui-fs` above, rather than run-scoped like `shakeScale`:
    // `MenuPad`'s stick quantiser reads it on the title and setup screens,
    // where no `Game` — and so no `Input` — exists yet (#192).
    setPadDeadzone(padDeadzoneFor(this.settings.padDeadzone));
  }

  /** Set by `startGame` while a run is live; cleared by `stopGame`. */
  private onRunSettingsChanged: (() => void) | null = null;

  // -- prologue -------------------------------------------------------------

  /**
   * docs/08 §2 — the prologue runs once per profile, before the setup screen.
   * You begin equipped and are stripped; the run's poverty is supposed to mean
   * something, which it cannot if it is the first thing you ever see.
   */
  private startPrologue(): void {
    const pro = new Game({
      canvas, uiRoot, seed: rollSeed(), scene: 'prologue',
      // Same single owner as a real run. The prologue writes `prologueSeen`
      // through this object, which is the flag the scene machine reads on the
      // very next transition — a second copy here is how that flag went missing.
      profile: this.profile, save: this.save,
    });
    pro.onPrologueDone = () => this.dispatch('prologueDone');
    // Quitting is legal from the prologue; opening settings is not (see the
    // transition table in `ui/scene.ts` — settings is reachable from the title
    // and from a paused run, and nowhere else). Leaving `onOpenSettings` null
    // is what makes that row absent from the prologue's pause menu rather than
    // present and refused, which is the same distinction the menu draws for
    // every other entry.
    pro.onQuitToTitle = () => this.dispatch('quitToTitle');
    // No ticker and no altar panel: the prologue's only readable surface is its
    // own prompts, which is the point (docs/08 §2 — teach with a key, not a
    // sentence).
    this.live = { game: pro, dispose: () => undefined };
    this.applyBindings();
    this.applyPadBindings();
    (window as unknown as { game: Game }).game = pro;
    pro.start();
  }

  // -- run setup ------------------------------------------------------------

  private showSetup(): void {
    injectRunSetupStyle();
    // A locked seed survives leaving and re-entering the screen, which is the
    // whole point of it: comparing two runs of the same world means starting
    // the second one after the first has ended. Rides in `settings`, the loose
    // string map, so there is no save-schema change — same precedent as
    // `prologueSeen`.
    const savedLock = this.profile.settings.seedLocked;
    this.seedLocked = savedLock === true;
    const savedSeed = Number(this.profile.settings.lockedSeed);
    if (this.seedLocked && Number.isInteger(savedSeed) && savedSeed >= 0) {
      this.seed = savedSeed >>> 0;
    } else {
      this.seed = rollSeed();
    }
    const rerender = (msg: string | null = null): void =>
      setup.update(buildRunSetupVM(this.prog, this.seed, msg, this.seedLocked));
    const rememberSeed = (): void => {
      this.profile.settings.seedLocked = this.seedLocked;
      this.profile.settings.lockedSeed = this.seed;
      this.persist();
    };
    const setup = new RunSetupScreen(uiRoot, {
      onReroll: () => {
        // Belt and braces over the disabled button: a locked seed that a
        // second input path could still reroll is not locked.
        if (this.seedLocked) return;
        this.seed = rollSeed();
        rerender();
      },
      onSeedEntered: (seed) => {
        this.seed = seed;
        if (this.seedLocked) rememberSeed();
        rerender();
      },
      onToggleSeedLock: (locked) => {
        this.seedLocked = locked;
        rememberSeed();
        rerender();
      },
      onPurchase: (id) => {
        // The only place Ash is spendable. Re-render with the outcome message so
        // a refusal says why rather than silently doing nothing.
        const res = buyFromAshShop(this.prog, id);
        this.profile.progression = this.prog.state;
        this.persist();
        rerender(res.message);
      },
      onToggleModifier: (id, on) => {
        const chosen = new Set(this.prog.effectiveWorldModifiers());
        if (on) chosen.add(id);
        else chosen.delete(id);
        const res = this.prog.setWorldModifiers([...chosen]);
        if (res.ok) {
          this.profile.progression = this.prog.state;
          this.persist();
        }
        rerender(res.ok ? null : (res.reason ?? null));
      },
      onStart: (choice) => {
        // Remember the pick so the next run defaults to it.
        this.prog.setStartKit(choice.kit);
        // #161: `setStartAbilities` had a caller nowhere — `createRunState`
        // derives `RunState.traversal.granted` from `effectiveStartAbilities()`,
        // which was therefore permanently empty, so the ledger disagreed with
        // the abilities the player actually started with.
        //
        // `resolveStartAbilities` answers a different question than
        // `setStartAbilities` asks — it returns the *held* set, innate ∪ picks,
        // for showing the player what the run starts with (its own header
        // comment: "not yet on the profile"); `setStartAbilities` wants only the
        // attunement-sourced picks, validated against slot count and what is
        // attuned, because `createRunState` unions the archetype's innates back
        // in itself. So the innate half is filtered back out here — the picker
        // does not stop a player from spending a slot on an ability their body
        // already has for free (the ability list is not archetype-scoped), and
        // that must not cost them the slot it looks like it cost.
        const held = resolveStartAbilities(this.prog, choice.archetype, choice.abilities);
        const innate = new Set<string>(getArchetype(choice.archetype).innateAbilities);
        this.prog.setStartAbilities(held.filter((id) => !innate.has(id)));
        this.persist();
        this.dispatch('runStarted');
        this.startGame({ ...choice, scene: 'world' });
      },
      onBack: () => this.dispatch('quitToTitle'),
    });
    this.setup = setup;
    rerender();
    setup.show();
  }

  // -- the run --------------------------------------------------------------

  private startGame(choice: StartChoice): void {
    const game = new Game({
      canvas,
      uiRoot,
      seed: choice.seed,
      scene: choice.scene,
      mode: choice.mode === 'blended' ? 'blended' : 'layered',
      archetype: choice.archetype,
      kit: choice.kit,
      startAbilities: choice.abilities,
      // One owner for the profile and the save (#51). The Game used to build
      // its own `SaveSystem` and load its own `ProfileData`, so a setting
      // changed mid-run was reverted by the next in-run autosave — the Game's
      // copy predated the change and knew nothing about it. Sharing the object
      // is what let the "write the settings again after stopGame" workaround go.
      profile: this.profile,
      // ...except the movement lab, which gets a `SaveSystem` with no storage.
      // The lab is a hand-built chamber with no enemies and no run, and its
      // `RunState` is indistinguishable on disk from a real one — `RunState`
      // does not record which world kind it was played in, so a lab run written
      // over a real one would come back through Continue as a world run in a
      // procedural cave. Refusing to write is the honest answer, and the title
      // already promises "no enemies, no run". Nothing it does to the shared
      // profile in memory outlives it either: `reload()` re-reads from disk.
      save: choice.scene === 'lab' ? new SaveSystem({ storage: null }) : this.save,
      // Present only for a Continue. Absent means "make a new one", which is
      // what `Game` did unconditionally for the whole project.
      run: choice.run,
    });

    // The two pause-menu entries that outlive the run, hung on the Game the way
    // `onPrologueDone` is. Assigned *after* construction, which is exactly why
    // `UIRoot` asks for them through a predicate when the menu is first shown.
    game.onOpenSettings = () => this.dispatch('openSettings');
    game.onQuitToTitle = () => this.dispatch('quitToTitle');

    const overlays = mountRunOverlays(game, () => this.settings, () => this.bindOverrides);
    this.onRunSettingsChanged = overlays.apply;
    this.live = { game, dispose: overlays.dispose };
    overlays.apply();
    // A fresh Game means a fresh Input, holding the shipped table. This is
    // where a key moved at the title screen reaches the run.
    this.appliedCodes = [];
    this.applyBindings();
    this.appliedPadButtons = [];
    this.applyPadBindings();

    game.start();
    boot.classList.add('gone');

    // Handy for poking at the simulation from the console during development.
    (window as unknown as { game: Game }).game = game;
  }

  private stopGame(): void {
    if (!this.live) return;
    // Write before tearing down, or Continue is a lie — issue #51.
    //
    // Nothing wrote the run on the way out. `Game` persists on a death, a stash
    // withdrawal and a craft, and `meta/save.ts`'s `Autosaver` and
    // `attachAutosaveHooks` — a complete, tested debounced autosave — have zero
    // call sites anywhere in `src/`. So quitting to the title left the run on
    // disk as of whenever the last of those three happened to fire, which for
    // most quits is "never". A Continue button offering that is worse than no
    // button. One write here closes the gap; wiring the `Autosaver` properly is
    // a separate issue and a `game.ts` change.
    this.live.game.persist();
    this.live.game.destroy();
    this.live.dispose();
    this.live = null;
    this.onRunSettingsChanged = null;
    // That Input is gone; the next one starts from the defaults again.
    this.appliedCodes = [];
    this.appliedPadButtons = [];

    // Wipe whatever the Game's UIRoot and the overlays left behind. UIRoot's
    // *window* listeners went with `Game.destroy()`, which destroys its UI —
    // the shell used to have to hand the keyboard back by hand here, and that
    // workaround is gone with the thing that made it necessary. Every shell
    // screen goes with the wipe and is rebuilt on
    // the next visit, because a screen holding element references into a
    // cleared host is a screen that renders into nothing.
    this.title?.destroy();
    this.settingsScreen?.destroy();
    this.setup?.destroy();
    this.title = null;
    this.settingsScreen = null;
    this.setup = null;
    uiRoot.textContent = '';

    this.reload();
  }

  /**
   * Re-read what the Game wrote, so the title screen reports the run that just
   * ended rather than the state of the world before it — and so Continue offers
   * the run the player just quit rather than the one before that.
   *
   * This used to end with a second `persistSettings()`, because the Game held
   * its own `ProfileData` and its last write would revert a setting changed
   * mid-run. `GameOptions.profile` retired that: the object the Game saved is
   * the object this shell wrote into, so the settings are already in the
   * envelope being read back here (#51).
   */
  private reload(): void {
    const loaded = this.save.load();
    this.profile = loaded.profile ?? createProfile();
    this.prog = new Progression(this.profile.progression);
    this.savedRun = isResumable(loaded.run) ? loaded.run : null;
    this.settings = readSettings(this.profile.settings);
    this.bindOverrides = readBindOverrides(this.profile.settings);
    this.padBindOverrides = readPadBindOverrides(this.profile.settings);
    this.applyGlobalSettings();
  }
}

function rollSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

// ------------------------------------------------------------- run overlays

/**
 * The DOM that rides alongside a live Game: the debug readout, the control
 * hints, the event ticker, the altar panel and the ability-cache prompt.
 *
 * Extracted from the old `startGame` so that quitting to the title can take it
 * all down again — it used to append into `#ui-root` and register a rAF loop
 * that nothing ever cancelled, which was survivable only because there was no
 * way to leave a run.
 *
 * Every one of these used to carry its own `position: fixed` and its own
 * hand-picked corner, and the control-hint line needed three of them before it
 * found one that did not print through something (#52). They claim regions now
 * — see `ui/hudLayout.ts` for the scheme and `OVERLAY_REGION` for this file's
 * five entries. Choosing a region is the whole placement decision; there is no
 * offset left to get wrong.
 */
function mountRunOverlays(
  game: Game,
  settings: () => Readonly<SettingsState>,
  binds: () => BindOverrides,
): { apply: () => void; dispose: () => void } {
  // Debug overlay. Kept as DOM rather than drawn in GL so it survives a
  // renderer failure — which is exactly when you most want to read it. It used
  // to start at top-left, on top of the vitals; `left-rail` is the region
  // underneath them and nothing else wants it.
  const dbg = document.createElement('pre');
  dbg.className = 'ovl-debug';
  dbg.style.display = 'none';
  claimRegion(uiRoot, 'left-rail').appendChild(dbg);

  // Control hints. Three positions were tried before this change — top-centre
  // (through the vitals), bottom (through the two weapon chips), top-right
  // (through the toasts, which is what the overlap check measured at 320x28 px
  // on every interface scale). `top-band` is the middle column of the top row,
  // so centring it there cannot reach either corner: the placement that failed
  // twice works now because the region constrains it, not because it was
  // nudged again.
  const help = document.createElement('div');
  help.className = 'ovl-hints';
  claimRegion(uiRoot, 'top-band').appendChild(help);

  /**
   * The hint line, rebuilt from the resolved bindings.
   *
   * It was a string literal saying "jump Space" until the settings screen
   * learned to move a key, at which point it became a legend that contradicted
   * the controls — which is the settings row lying one screen away from where
   * the player set it. Rebuilt on every `apply` so a key moved from the pause
   * menu is corrected the moment the menu closes.
   */
  const renderHelp = (binds: BindOverrides): void => {
    const k = new Map(bindingRows(DEFAULT_BINDINGS, binds).map((r) => [r.action, r.key]));
    const b = (s: string): string => `<b>${escapeHtml(s)}</b>`;
    const key = (action: string): string => b(k.get(action) ?? '—');
    // `grip` earns a slot here because it is the one verb in the game you
    // cannot discover by pressing things: held on a wall it clings, held on the
    // ground it opens the gathering field (#122), and neither is visible until
    // you already hold it in the right place. Everything else on this line is a
    // reminder; this one is the only way to find out.
    const html =
      `move ${b('A/D')} &nbsp; jump ${key('jump')} &nbsp; ` +
      `dash ${key('dash')} &nbsp; attack ${key('mainAttack')}/${key('offAttack')}<br>` +
      `grip/gather ${key('grip')} &nbsp; loadout ${b('Tab')} &nbsp; codex ${b('C')} &nbsp; ` +
      `pause ${b('Esc')} &nbsp; debug ${b('F3')}`;
    if (help.innerHTML !== html) help.innerHTML = html;
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'F3') {
      e.preventDefault();
      game.showDebug = !game.showDebug;
      dbg.style.display = game.showDebug ? 'block' : 'none';
    }
  };
  window.addEventListener('keydown', onKey);

  /**
   * The run log — docs/01 §6.4.1 and docs/06 §4.
   *
   * This is the feature the Noita postmortem argues is worth more than extra
   * simulation: their wet-resists-ignition rule was broken for two months and
   * nobody noticed, because the UI still said WET. A five-step chain reaction
   * is magic until it is a sentence; this is what makes it a sentence.
   *
   * **It claims `top-right`, the same region as the toast stack, and that is the
   * answer to #52's "the run log and the event ticker are the same information
   * in two places".** They are one feed with two salience tiers: a toast is loud,
   * bordered and gone in 4.5 s; a log line is quiet, ellipsised and persists for
   * five. Stacked in one column they cannot collide at any width or scale, and
   * the duplication is now legible instead of being two corners apart.
   *
   * What this cannot fix from here is the *content* duplication underneath it:
   * seven call sites in `core/game.ts` emit both a toast and a log line for the
   * same event (see the handoff note). Merging the surfaces is the half that
   * lives in the UI layer; collapsing the seven pairs is a `game.ts` hunk.
   *
   * Dirty-checked against a sequence number so it costs nothing on quiet frames.
   */
  const ticker = document.createElement('div');
  ticker.className = 'ovl-log';
  claimRegion(uiRoot, 'top-right').appendChild(ticker);
  let lastSeq = -1;

  /**
   * The altar panel. Shows all three offers at once with their rolled deltas,
   * because the choice is a *comparison* — "94 damage" is unjudgeable alone,
   * and docs/04 §7 makes side-by-side comparison a first-class affordance
   * rather than a menu feature.
   */
  const altarPanel = document.createElement('div');
  altarPanel.className = 'ovl-prompt';
  altarPanel.style.display = 'none';
  claimRegion(uiRoot, 'centre-prompt').appendChild(altarPanel);

  let lastAltarKey = '';
  const renderAltar = (): void => {
    const altar = game.altar;
    if (!altar || !altar.active) {
      if (altarPanel.style.display !== 'none') altarPanel.style.display = 'none';
      return;
    }
    const st = altar.status();
    if (!st.offer) return;

    // Stats appear only on hover: out of range the plinth shows just its name
    // and the clock. Reading the numbers should require walking over, which
    // makes approaching the altar an action rather than a menu.
    const key = `${st.rerolls}|${st.inRange}|${Math.ceil(st.timeToReroll)}`;
    if (key === lastAltarKey) return;
    lastAltarKey = key;

    const o = st.offer;
    altarPanel.style.display = 'flex';

    if (!st.inRange) {
      altarPanel.innerHTML =
        `<div style="border:1px solid #262b36;background:rgba(11,12,17,.78);` +
        `padding:5px 10px;text-align:center;opacity:.8">` +
        `<div style="color:#e0a860;text-transform:uppercase;letter-spacing:.14em">` +
        `${escapeHtml(o.resolved.def.name)}</div>` +
        `<div style="color:#5b5750">turns over in ` +
        `<b style="color:#8d8779">${Math.ceil(st.timeToReroll)}s</b></div></div>`;
      return;
    }

    const deltas = rollDeltas(o)
      .map((d) => {
        const c = d.pct >= 0.5 ? '#7fd17f' : d.pct <= -0.5 ? '#d2544a' : '#5b5750';
        const sign = d.pct >= 0 ? '+' : '';
        return `<span style="color:${c}">${d.label} ${sign}${d.pct.toFixed(0)}%</span>`;
      })
      .join(' ');

    altarPanel.innerHTML =
      `<div style="border:1px solid #e0a860;background:rgba(11,12,17,.94);` +
      `padding:6px 11px;min-width:210px;box-shadow:0 0 0 1px #e0a86055">` +
      `<div style="color:#e0a860;text-transform:uppercase;letter-spacing:.14em">` +
      `${escapeHtml(o.resolved.def.name)}` +
      `<span style="color:#5b5750;letter-spacing:.06em"> · ${escapeHtml(String(o.resolved.def.class))}</span>` +
      `</div>` +
      `<div style="color:#8d8779">${escapeHtml(describeOffer(o))}</div>` +
      `<div style="margin-top:2px">${deltas}</div>` +
      `<div style="color:#e0a860;margin-top:3px">▸ E to take` +
      `<span style="color:#5b5750"> · turns over in ${Math.ceil(st.timeToReroll)}s</span>` +
      `</div></div>`;
  };

  /**
   * The ability-cache prompt. Deliberately the altar's shape — one thing, in
   * the world, claimed by standing at it — because docs/11 argues that reusing
   * a strong interaction beats adding a second weaker one.
   *
   * It shared the altar's `bottom: 132px` anchor, which was fine right up until
   * a room held both — then the two printed through each other with nothing to
   * say which was which. Same region, stacked, so that is no longer possible.
   */
  const cachePanel = document.createElement('div');
  cachePanel.className = 'ovl-prompt';
  cachePanel.style.display = 'none';
  claimRegion(uiRoot, 'centre-prompt').appendChild(cachePanel);
  let lastCacheId = '';

  const renderCache = (): void => {
    const c = game.cacheInRange;
    if (!c) {
      if (cachePanel.style.display !== 'none') cachePanel.style.display = 'none';
      lastCacheId = '';
      return;
    }
    if (c.abilityId === lastCacheId) return;
    lastCacheId = c.abilityId;
    const t = ABILITY_TEXT[c.abilityId];
    cachePanel.style.display = 'block';
    cachePanel.innerHTML =
      `<div style="border:1px solid #7fd17f;background:rgba(11,12,17,.94);` +
      `padding:6px 11px;min-width:210px;text-align:center;box-shadow:0 0 0 1px #7fd17f55">` +
      `<div style="color:#7fd17f;text-transform:uppercase;letter-spacing:.14em">` +
      `${escapeHtml(t.name)}</div>` +
      `<div style="color:#8d8779">opens ${escapeHtml(t.opens)}</div>` +
      `<div style="color:#7fd17f;margin-top:3px">▸ E to take</div></div>`;
  };

  let raf = 0;
  let alive = true;
  const refresh = (): void => {
    if (!alive) return;
    renderAltar();
    renderCache();

    if (game.eventSeq !== lastSeq) {
      lastSeq = game.eventSeq;
      const recent = game.events.slice(-5);
      ticker.innerHTML = recent
        .map((line, k) => {
          // Newest at the bottom, fading upward — the eye lands on the latest.
          // Nowrap/ellipsis moved to `.ovl-log > div`; the fade is per-line and
          // has to stay inline.
          const op = 0.32 + (k / Math.max(1, recent.length - 1)) * 0.68;
          return `<div style="opacity:${op.toFixed(2)}">${escapeHtml(line)}</div>`;
        })
        .join('');
    }

    if (game.showDebug) {
      const recent = game.events.slice(-6).join('\n  ');
      dbg.textContent =
        `${profiler.report()}\n` +
        `  cells ${game.cells.countNonEmpty()}  chunks ${game.cells.chunkCount}` +
        `  active ${game.cells.activeChunks.length}  parts ${game.particles.count}\n` +
        `  state ${game.mover.state}  grounded ${game.entities.grounded[game.playerIdx]}` +
        `  vx ${game.entities.vx[game.playerIdx].toFixed(0)}` +
        `  vy ${game.entities.vy[game.playerIdx].toFixed(0)}\n` +
        // The only place a refused non-finite coordinate becomes visible to a
        // human (#76). Both records are sticky for the life of the page — a
        // fault that scrolled past in one frame is exactly the thing that hid
        // this producer through all of #71 — so the first one of a session is
        // still on screen an hour later. Absent entirely while nothing is wrong,
        // so it costs the overlay no space in the normal case.
        (nonFiniteLine(game) ? `  ${nonFiniteLine(game)}\n` : '') +
        (recent ? `  ${recent}` : '');
    }
    raf = requestAnimationFrame(refresh);
  };
  refresh();

  /**
   * Push the run-scoped settings at the things they actually control. Called
   * once at mount and again on every change, which is why the ticker toggle is
   * observable the instant it moves rather than on the next run.
   */
  const apply = (): void => {
    const s = settings();
    ticker.style.display = s.ticker === 'on' ? 'block' : 'none';
    help.style.display = s.hints === 'on' ? 'block' : 'none';
    game.showDebug = s.debugOverlay === 'on';
    dbg.style.display = game.showDebug ? 'block' : 'none';
    renderHelp(binds());
    // `Game.camera` is public and every `shake()` call inside the run is scaled
    // by this, so the row needs nothing from `core/game.ts`. Applied here rather
    // than at construction so turning it down from the pause menu is visible on
    // the very next hit.
    game.camera.shakeScale = shakeScaleFor(s.screenShake);
  };

  const dispose = (): void => {
    alive = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('keydown', onKey);
    for (const e of [dbg, help, ticker, altarPanel, cachePanel]) e.remove();
  };

  return { apply, dispose };
}

// ---------------------------------------------------------------------- boot

function main(): void {
  const params = new URLSearchParams(location.search);
  const seedParam = params.get('seed');

  // The query-string bypass predates the title screen and still earns its keep:
  // automated checks and the movement lab need to reach a Game directly, with a
  // fixed archetype and seed and no menus in the way.
  const explicitScene = params.get('scene');
  const bypass = explicitScene !== null || params.has('mode') || params.has('archetype');

  if (bypass) {
    boot.classList.add('gone');
    // The stored settings still apply here — the bypass skips the menus, not
    // the player's choices.
    const save = new SaveSystem();
    const profile = save.load().profile ?? createProfile();
    const storedRaw = profile.settings;
    const stored = readSettings(storedRaw);
    const storedBinds = readBindOverrides(storedRaw);
    const storedPadBinds = readPadBindOverrides(storedRaw);
    const isLab = explicitScene === 'lab';
    uiRoot.style.setProperty('--ui-fs', `${uiScalePx(stored.uiScale)}px`);
    if (stored.contrast === 'high') {
      for (const k in CONTRAST_VARS) uiRoot.style.setProperty(k, CONTRAST_VARS[k]);
    }
    setPadDeadzone(padDeadzoneFor(stored.padDeadzone));
    const game = new Game({
      canvas,
      uiRoot,
      seed: seedParam ? Number(seedParam) >>> 0 : rollSeed(),
      scene: isLab ? 'lab' : 'world',
      mode: params.get('mode') === 'blended' ? 'blended' : 'layered',
      archetype: params.get('archetype') ?? 'duelist',
      kit: params.get('kit') ?? 'kit.wanderer',
      startAbilities: [],
      // One `ProfileData` here too, rather than the Game loading a second copy
      // of the one we just read for the settings. No run is passed: the bypass
      // exists to reach a Game with a *fixed* seed, and resuming would silently
      // ignore the `?seed=` that is the whole point of it.
      profile,
      // Same reason as the shell's lab branch: a lab run must never land on
      // disk where Continue would offer it as a world run.
      save: isLab ? new SaveSystem({ storage: null }) : save,
    });
    const overlays = mountRunOverlays(game, () => stored, () => storedBinds);
    overlays.apply();
    // The bypass skips the menus, not the player's choices — including the keys
    // they moved.
    const resolved = resolveBindings(DEFAULT_BINDINGS, storedBinds);
    for (const code of Object.keys(DEFAULT_BINDINGS)) game.input.unbind(code);
    for (const code in resolved) game.input.rebind(code, resolved[code]);
    const resolvedPad = resolvePadBindings(DEFAULT_PAD_BINDINGS, storedPadBinds);
    for (const button of Object.keys(DEFAULT_PAD_BINDINGS)) game.input.unbindPad(Number(button));
    for (const button in resolvedPad) game.input.rebindPad(Number(button), resolvedPad[button]);
    game.start();
    (window as unknown as { game: Game }).game = game;
    return;
  }

  const shell = new Shell();
  (window as unknown as { shell: Shell }).shell = shell;
  shell.boot();
}

try {
  main();
} catch (err) {
  fail(err);
}

window.addEventListener('error', (e) => fail(e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => fail(e.reason));
